<script setup lang="ts">
import { isNull } from 'lodash'
import { ref, computed, onMounted, watch, onUnmounted } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { ChevronUpDownIcon, UserIcon, KeyIcon } from '@heroicons/vue/16/solid'
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from '@headlessui/vue'
import { CheckIcon } from '@heroicons/vue/20/solid'
import {
  type BannerSettings,
  type State,
  SignInViewBanner
} from '@/components/views/interfaces/sign-in-view.interfaces.ts'
import { AuthorizeUserMutation } from '@/components/users/graphql/user.mutations.ts'
import { BackendErrorCode, Environment } from '@/enums.ts'
import { LOCAL_STORAGE_CROWDY_API_ENV_KEY } from '@/config/config.ts'
import { DEFAULT_ENVIRONMENT, ENVIRONMENTS } from '@/config/env.const.ts'
import { type IEnvironment } from '@/interfaces.ts'
import { setDefaultApolloClient } from '@/graphql.ts'
import { useTranslations } from '@/composables/useTranslations.ts'
import { usePageTitle } from '@/composables/usePageTitle.ts'
import type { AuthorizeUserResponse, AuthorizeUserVariables } from '@/components/users/interfaces.ts'
import { isNumeric } from '@/utils/util.ts'

// State, stores, settings
const state: State = {
  banners: {
    [SignInViewBanner.WrongCredentials]: { isVisible: ref(true) },
    [SignInViewBanner.InternalServerError]: { isVisible: ref(true) },
    [SignInViewBanner.NetworkError]: { isVisible: ref(true) },
    [SignInViewBanner.MaintenanceMode]: { isVisible: ref(true) }, // Not implemented, backend works required. + Better implement it without button click
    [SignInViewBanner.UnknownError]: { isVisible: ref(true) },
    [SignInViewBanner.PasswordResetSent]: { isVisible: ref(true) },
    [SignInViewBanner.AccountDeactivated]: { isVisible: ref(true) },
  },
  currentBanner: ref<SignInViewBanner | null>(null)
}

const INITIAL_ANIMATION_DURATION_MS = 3000
const username = defineModel('username', { default: '' })
const password = defineModel('password', { default: '' })
const gqlVariables = computed<AuthorizeUserVariables>(() => {
  return {
    input: {
      username: username.value,
      password: password.value
    }
  }
})
const isEnvironmentDropdownVisible = ref<boolean>(false)
const isInitialFormAnimationVisible = ref<boolean>(true)
const isLoginFailedAnimationVisible = ref<boolean>(false)
const emit = defineEmits(['onLoginSucceed'])
const envs = computed(() => ENVIRONMENTS)
const { t } = useTranslations()
const selectedEnv = ref<IEnvironment>(ENVIRONMENTS.find(({ env }) => {
  return env === (localStorage.getItem(LOCAL_STORAGE_CROWDY_API_ENV_KEY) ?? DEFAULT_ENVIRONMENT)
}) as IEnvironment)
const bannersSettings = computed(() => ({
  [SignInViewBanner.WrongCredentials]: {
    isVisible: state.banners[SignInViewBanner.WrongCredentials].isVisible.value,
    text: t('signIn.banners.wrongUsernameOrPassword'),
    rootCssClasses: 'flex items-center gap-x-6 bg-red-600 px-6 py-2.5 sm:px-3.5 sm:before:flex-1 w-full fixed',
  },
  [SignInViewBanner.InternalServerError]: {
    isVisible: state.banners[SignInViewBanner.InternalServerError].isVisible.value,
    text: t('signIn.banners.notWorking'),
    rootCssClasses: 'flex items-center gap-x-6 bg-red-600 px-6 py-2.5 sm:px-3.5 sm:before:flex-1 w-full fixed',
  },
  [SignInViewBanner.NetworkError]: {
    isVisible: state.banners[SignInViewBanner.NetworkError].isVisible.value,
    text: t('signIn.banners.networkError'),
    rootCssClasses: 'flex items-center gap-x-6 bg-red-600 px-6 py-2.5 sm:px-3.5 sm:before:flex-1', // Not used atm
  },
  [SignInViewBanner.MaintenanceMode]: {
    isVisible: state.banners[SignInViewBanner.MaintenanceMode].isVisible.value,
    text: t('signIn.banners.maintenanceIsOn'),
    rootCssClasses: 'flex items-center gap-x-6 bg-red-600 px-6 py-2.5 sm:px-3.5 sm:before:flex-1', // Not used atm
  },
  [SignInViewBanner.UnknownError]: {
    isVisible: state.banners[SignInViewBanner.UnknownError].isVisible.value,
    text: t('signIn.banners.unknownError'),
    rootCssClasses: 'flex items-center gap-x-6 bg-red-600 px-6 py-2.5 sm:px-3.5 sm:before:flex-1', // Not used atm
  },
  [SignInViewBanner.PasswordResetSent]: {
    isVisible: state.banners[SignInViewBanner.PasswordResetSent].isVisible.value,
    text: t('signIn.banners.instructionsResetPassword'),
    rootCssClasses: 'flex items-center gap-x-6 bg-green-600 px-6 py-2.5 sm:px-3.5 sm:before:flex-1 w-full fixed'
  },
  [SignInViewBanner.AccountDeactivated]: {
    isVisible: state.banners[SignInViewBanner.AccountDeactivated].isVisible.value,
    text: t('signIn.banners.userAccountDeactivated'),
    rootCssClasses: 'flex items-center gap-x-6 bg-pink-600 px-6 py-2.5 sm:px-3.5 sm:before:flex-1 w-full fixed'
  },
}))

// Queries
const {
  onDone: onLoginSucceed,
  onError: onLoginFailed,
  mutate: logIn
} = useMutation<{ user: AuthorizeUserResponse }, AuthorizeUserVariables>(AuthorizeUserMutation)

onLoginSucceed(({ data }) => {
  emit('onLoginSucceed', data?.user)
})

onLoginFailed((err) => {
  console.log('onLoginFailed | err:', err.message)
  const errorCodesMap = {
    [BackendErrorCode.UserNotFoundOrWrongPassword]: SignInViewBanner.WrongCredentials,
    [BackendErrorCode.ErrUserAccountDeactivated]: SignInViewBanner.AccountDeactivated
  }

  // @ts-expect-error it's okay lazy to make proper types
  setCurrentBanner(errorCodesMap[err.message] ?? SignInViewBanner.InternalServerError)

  if (err.message === BackendErrorCode.UserNotFoundOrWrongPassword) {
    setLoginAnimationVisibility(true)
    setTimeout(() => {
      setLoginAnimationVisibility(false)
    }, 1000)
  } else {
    console.error('SignInView | Error:', err)
  }
})

// Computed
const isSignInButtonEnabled =  computed(() => {
  // TODO: Read lenghts form configs
  return username.value.length >= 3 && password.value.length >= 8
})
const currentBanner = computed<BannerSettings | null>(() => {
  return isNull(state.currentBanner.value) ? null : bannersSettings.value[state.currentBanner.value]
})

// Methods
function setCurrentBanner(banner: SignInViewBanner | null) {
  state.currentBanner.value = banner
}

function setLoginAnimationVisibility (flag: boolean) {
  isLoginFailedAnimationVisible.value = flag
}

function setInitialFormAnimationVisibility (flag: boolean) {
  isInitialFormAnimationVisible.value = flag
}

function onLogoClickedWithPressedAltButton () {
  isEnvironmentDropdownVisible.value = !isEnvironmentDropdownVisible.value
}

function hideCurrentBannerIfWrongCredentials (): void {
  if (state.currentBanner.value === SignInViewBanner.WrongCredentials) {
    setCurrentBanner(null)
  }
}

/* function onForgotPassword() {
  setCurrentBanner(SignInViewBanner.PasswordResetSent)
} */

onMounted(() => {
  setTimeout(() => { setInitialFormAnimationVisibility(false) }, INITIAL_ANIMATION_DURATION_MS)

  const { stop: stopUsernameWatcher } = watch(username, () => { hideCurrentBannerIfWrongCredentials() })
  const { stop: stopPasswordWatcher } = watch(password, () => { hideCurrentBannerIfWrongCredentials() })
  const { stop: stopSelectedEnvWatcher } = watch(selectedEnv, (newEnv) => {
    setDefaultApolloClient(newEnv.env as Environment)
    localStorage.setItem(LOCAL_STORAGE_CROWDY_API_ENV_KEY, newEnv.env)
  })

  onUnmounted(() => {
    stopUsernameWatcher()
    stopPasswordWatcher()
    stopSelectedEnvWatcher()
  })
})

const title = computed(() => `${t('signIn.signIn')}`)
usePageTitle(title)
</script>
<template>
  <transition name="banner-fade">
    <div v-if="currentBanner" :class="currentBanner.rootCssClasses">
      <p class="text-sm/6 text-white">{{ currentBanner.text }}</p>
      <div class="flex flex-1 justify-end">
        <button
          type="button"
          class="-m-3 p-3 focus-visible:-outline-offset-4"
          @click="setCurrentBanner(null)"
        >
          <span class="sr-only">{{ t('signIn.dismiss') }}</span>
          <XMarkIcon class="size-5 text-white" aria-hidden="true" />
        </button>
      </div>
    </div>
  </transition>
  <!-- isLoginFailedAnimationVisible-->
  <div
    :class="{
      'flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8 bg-red animate__animated': true,
      'animate__fadeIn': isInitialFormAnimationVisible,
      'animate__headShake': isLoginFailedAnimationVisible
    }">
    <div class="sm:mx-auto sm:w-full sm:max-w-sm">
      <img
        class="h-max w-8/12 m-auto"
        src="/crowdy-logo400rect.png"
        alt="crowdy.ai support agent"
        @click.alt.exact="onLogoClickedWithPressedAltButton"
      />
      <h3 class="mt-3.5 text-center font-light tracking-tight text-gray-900">
        {{ t('signIn.signInToYourAccount') }}
      </h3>
    </div>
    <div class="mt-2 sm:mx-auto sm:w-full sm:max-w-sm">
      <form class="space-y-3">
        <div>
          <div>
            <label for="username" class="block text-sm/6 font-medium text-gray-900">
              {{ t('signIn.username') }}
            </label>
            <div class="mt-2 grid grid-cols-1">
              <input
                v-model="username"
                autocomplete="username"
                @keyup.enter="() => {
                  if (!isSignInButtonEnabled) return
                  logIn({ variables: gqlVariables })
                }"
                type="text"
                name="username"
                id="username"
                :required="true"
                class="col-start-1 row-start-1 block w-full rounded-md font-medium bg-white py-1.5 pl-10 pr-3 text-base text-gray-700 outline-solid outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-solid focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:pl-9 sm:text-sm/6"
              />
              <UserIcon class="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-gray-400 sm:size-4" aria-hidden="true" />
            </div>
          </div>
        </div>
        <div>
          <div class="flex items-center justify-between">
            <label for="password" class="block text-sm/6 font-medium text-gray-900">
              {{ t('signIn.password') }}
            </label>
<!--            <div class="text-sm">-->
<!--              <a-->
<!--                href="#"-->
<!--                class="font-semibold text-indigo-600 hover:text-indigo-500"-->
<!--                @click.prevent="onForgotPassword"-->
<!--              >-->
<!--                {{ t('signIn.forgotPassword') }}-->
<!--              </a>-->
<!--            </div>-->
          </div>
          <div class="mt-2 grid grid-cols-1">
            <input
              v-model="password"
              @keyup.enter="() => {
                if (!isSignInButtonEnabled) return
                logIn({ variables: gqlVariables })
              }"
              type="password"
              name="password"
              id="password"
              :required="true"
              autocomplete="current-password"
              class="col-start-1 row-start-1 block w-full rounded-md font-medium bg-white py-1.5 pl-10 pr-3 text-base text-gray-900 outline-solid outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-solid focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:pl-9 sm:text-sm/6"
            />
            <KeyIcon class="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-gray-400 sm:size-4" aria-hidden="true" />
          </div>
        </div>
        <div v-if="isEnvironmentDropdownVisible">
          <Listbox as="div" v-model="selectedEnv">
            <ListboxLabel class="block text-sm/6 font-medium text-gray-900">
              {{ t('signIn.environment') }}
            </ListboxLabel>
            <div class="relative mt-2">
              <ListboxButton class="grid w-full cursor-default grid-cols-1 rounded-md bg-white py-1.5 pl-3 pr-2 text-left text-gray-900 outline-solid outline-1 -outline-offset-1 outline-gray-300 focus:outline-solid focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6">
                <span class="col-start-1 row-start-1 truncate pr-6">{{ selectedEnv.title }}</span>
                <ChevronUpDownIcon class="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 sm:size-4" aria-hidden="true" />
              </ListboxButton>

              <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                <ListboxOptions class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm">
                  <ListboxOption as="template" v-for="env in envs" :key="env.env" :value="env" v-slot="{ active, selected }">
                    <li :class="[active ? 'bg-indigo-600 text-white outline-hidden' : 'text-gray-900', 'relative cursor-default select-none py-2 pl-8 pr-4']">
                      <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">{{ env.title }}</span>

                      <span v-if="selected" :class="[active ? 'text-white' : 'text-indigo-600', 'absolute inset-y-0 left-0 flex items-center pl-1.5']">
                <CheckIcon class="size-5" aria-hidden="true" />
              </span>
                    </li>
                  </ListboxOption>
                </ListboxOptions>
              </transition>
            </div>
          </Listbox>
        </div>
        <div class="py-5">
          <button
            @click="logIn({ variables: gqlVariables })"
            :disabled="!isSignInButtonEnabled && !isLoginFailedAnimationVisible"
            type="button"
            :class="{
              'flex w-full justify-center rounded-md px-3 py-1.5 text-sm/6 font-medium text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600': true,
              'bg-indigo-600': isSignInButtonEnabled && !isLoginFailedAnimationVisible,
              'bg-indigo-300': !isSignInButtonEnabled || isLoginFailedAnimationVisible
            }"
          >
            {{ t('signIn.signIn') }}
          </button>
        </div>
      </form>
      <!--<p class="mt-10 text-center text-sm/6 text-gray-500">
        Not a member?
        {{ ' ' }}
        <a href="#" class="font-semibold text-indigo-600 hover:text-indigo-500">Start a 14 day free trial</a>
      </p>-->
    </div>
  </div>
</template>
<style scoped>
/* This only changes this particular animation duration */
.animate__animated.animate__fadeIn {
  --animate-duration: 3s;
}
.animate__animated.animate__headShake {
  --animate-duration: 1s;
}
.banner-fade-enter-active, .banner-fade-leave-active {
  transition: opacity 0.3s;
}
.banner-fade-enter-from, .banner-fade-leave-to {
  opacity: 0;
}
</style>
